import React, { useEffect, useState } from 'react';
import {
    Container, Box, Typography, Button, TextField, IconButton,
    Avatar, Grid, Paper, LinearProgress, useTheme, useMediaQuery,
    Card, CardContent, Divider,
    Dialog, ToggleButton, ToggleButtonGroup, Chip
} from '@mui/material';
import { Add, CloudUpload, Edit, Delete as DeleteIcon, Close } from '@mui/icons-material';
import api from '../../utils/api';

const textFieldStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
        },
    }
};

const buildUrl = (filename) => {
  if (!filename) return '';
  if (/^https?:/.test(filename) || filename.startsWith('data:')) return filename;
  const base = process.env.REACT_APP_FILE_BASE_URL || '';
  return `${base}/uploads/documents/${filename}`;
};

const getFileType = (filename) => {
  if (!filename) return 'image';
  const ext = filename.split('.').pop().toLowerCase();
  if (["mp4","webm","ogg"].includes(ext)) return 'video';
  return 'image';
};

// تبدیل مقادیر inApp/inWeb به یک مقدار واحد برای انتخابگر محیط
const getEnv = (item = {}) => {
  const inApp = item.inApp !== false;
  const inWeb = item.inWeb !== false;
  if (inApp && inWeb) return 'both';
  if (inApp) return 'app';
  return 'web';
};

const ENV_LABELS = {
  both: 'اپ و وب',
  app: 'فقط اپ',
  web: 'فقط وب',
};

const SliderManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [slides, setSlides] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const normalizeSlides = (items = []) => items
    .map((item) => ({
      ...item,
      id: item._id || item.id,
      order: Number(item.order) || 0,
    }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const loadSlides = async () => {
    try {
      const resp = await api.get('/slider');
      const data = Array.isArray(resp.data) ? resp.data : [];
      setSlides(normalizeSlides(data));
    } catch (error) {
      console.error('Unable to load slider data:', error);
      setSlides([]);
    }
  };

  useEffect(() => {
    loadSlides();
  }, []);

  const handleAdd = () => setEditing({ title: '', image: '', images: null, link: '', order: slides.length + 1, type: 'image', inApp: true, inWeb: true });

  const handleSaveEdit = async () => {
    if (!editing) return;
    const env = getEnv(editing);
    const payload = {
      title: editing.title,
      image: editing.image,
      images: editing.images || undefined,
      link: editing.link,
      order: Number(editing.order) || 0,
      isActive: true,
      type: getFileType(editing.image),
      inApp: env === 'both' || env === 'app',
      inWeb: env === 'both' || env === 'web',
    };

    try {
      if (editing.id) {
        await api.patch(`/slider/${editing.id}`, payload);
      } else {
        await api.post('/slider', payload);
      }
      await loadSlides();
      setEditing(null);
    } catch (error) {
      console.error('Unable to save slide:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا حذف شود؟')) return;
    try {
      await api.delete(`/slider/${id}`);
      await loadSlides();
    } catch (error) {
      console.error('Unable to delete slide:', error);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    // محدودیت حجم ۲۰ مگابایت
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('حجم فایل نباید بیشتر از 20 مگابایت باشد.');
      return null;
    }
    const fd = new FormData(); fd.append('file', file);
    setUploadProgress(0);
    try {
      const res = await api.post('/slider/upload-image', fd, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        }
      });
      setUploadProgress(0);
      // { filename, images: { mobile, tablet, laptop, desktop } | null }
      return res.data;
    } catch (e) { setUploadProgress(0); console.error(e); return null; }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'} 
        justifyContent="space-between" 
        alignItems={isMobile ? 'stretch' : 'center'} 
        gap={2}
        mb={3}
      >
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">مدیریت اسلایدر</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={handleAdd}
          fullWidth={isMobile}
          sx={{ borderRadius: 3, px: 3 }}
        >
          اسلاید جدید
        </Button>
      </Box>

      <Grid container spacing={2}>
        {[...slides].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)).map(s => (
          <Grid item xs={12} sm={6} md={4} key={s.id}>
            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%' }}>
              <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: 'grey.100' }}>
                {getFileType(s.image) === 'video' ? (
                  <video 
                    src={buildUrl(s.image)} 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
                    controls 
                    preload="metadata" 
                  />
                ) : (
                  <img 
                    src={buildUrl(s.image)} 
                    alt={s.title} 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                )}
              </Box>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {s.title || 'بدون عنوان'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      ترتیب نمایش: #{s.order ?? 0}
                    </Typography>
                    <Chip
                      label={ENV_LABELS[getEnv(s)]}
                      size="small"
                      color={getEnv(s) === 'both' ? 'success' : 'default'}
                      sx={{ mt: 0.5, height: 22 }}
                    />
                  </Box>
                  <Box>
                      <IconButton color="primary" onClick={() => setEditing({ ...s })} size="small" sx={{ bgcolor: 'primary.lighter', mr: 0.5 }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(s.id)} size="small" sx={{ bgcolor: 'error.lighter' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {slides.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed #ccc' }}>
              <Typography color="textSecondary">هیچ اسلایدی وجود ندارد.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {editing && (
        <Dialog 
          open={!!editing} 
          onClose={() => setEditing(null)} 
          maxWidth="sm" 
          fullWidth 
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                {editing.id ? 'ویرایش اسلاید' : 'افزودن اسلاید جدید'}
              </Typography>
              {isMobile && <IconButton onClick={() => setEditing(null)}><Close /></IconButton>}
            </Box>
            
            <TextField fullWidth label="عنوان" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} sx={textFieldStyle} />
            <TextField fullWidth label="لینک اپ (مثال: /profile یا https://...)" value={editing.link} onChange={(e) => setEditing({ ...editing, link: e.target.value })} sx={textFieldStyle} />

            <TextField
              fullWidth
              label="ترتیب نمایش"
              type="number"
              value={editing.order ?? 0}
              onChange={(e) => setEditing({ ...editing, order: e.target.value })}
              sx={textFieldStyle}
            />

            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              نمایش در کدام محیط؟
            </Typography>
            <ToggleButtonGroup
              value={getEnv(editing)}
              exclusive
              fullWidth
              color="primary"
              onChange={(e, value) => {
                if (!value) return;
                setEditing({
                  ...editing,
                  inApp: value === 'both' || value === 'app',
                  inWeb: value === 'both' || value === 'web',
                });
              }}
              sx={{ mb: 2, '& .MuiToggleButton-root': { borderRadius: '12px', py: 1 } }}
            >
              <ToggleButton value="both">اپ و وب</ToggleButton>
              <ToggleButton value="app">فقط اپ</ToggleButton>
              <ToggleButton value="web">فقط وب</ToggleButton>
            </ToggleButtonGroup>

            <Box 
              sx={{ 
                p: 2, 
                border: '2px dashed', 
                borderColor: 'divider', 
                borderRadius: 3, 
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Button variant="contained" component="label" startIcon={<CloudUpload />} sx={{ borderRadius: 2 }}>
                آپلود تصویر یا ویدیو
                <input hidden type="file" accept="image/*,video/mp4,video/webm,video/ogg" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const uploaded = await uploadFile(f);
                  if (uploaded?.filename) setEditing({ ...editing, image: uploaded.filename, images: uploaded.images || null });
                }} />
              </Button>
              
              {uploadProgress > 0 && uploadProgress < 100 && (
                <Box width="100%">
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="caption" color="textSecondary">{uploadProgress}%</Typography>
                </Box>
              )}
              
              {editing.image && (
                <Box sx={{ position: 'relative', width: '100%', maxWidth: 300, borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
                  {getFileType(editing.image) === 'video' ? (
                    <video src={buildUrl(editing.image)} style={{ width: '100%', aspectRatio: '16/9' }} controls preload="metadata" />
                  ) : (
                    <img src={buildUrl(editing.image)} alt="Preview" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }} />
                  )}
                </Box>
              )}
            </Box>
            
            <Box mt={3} display="flex" gap={2}>
              <Button fullWidth variant="contained" onClick={handleSaveEdit} sx={{ borderRadius: 2, py: 1.5 }}>ذخیره</Button>
              {!isMobile && <Button fullWidth variant="outlined" onClick={() => setEditing(null)} sx={{ borderRadius: 2, py: 1.5 }}>انصراف</Button>}
            </Box>
          </Box>
        </Dialog>
      )}
    </Container>
  );
};

export default SliderManager;
